/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import type { CatalogConfigsData, CatalogConfigsOptions } from '../interface';
import { type AxiosInstance } from 'axios';
import type {
  GetCatalogConfigsParamsModel,
  CatalogConfigResponseModel,
} from '@/api/types/product-data-types';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { useLocale } from '@/hooks/useLocale';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useFeatureFlag } from '@/features/globalConfig';
import { type QueryClient } from '@tanstack/react-query';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/catalog-configs`;

const getCatalogConfigs = async (
  options: CatalogConfigsOptions,
  axiosInstance?: AxiosInstance
): Promise<CatalogConfigsData> => {
  const { country, itemId, itemType } = options;

  const params: GetCatalogConfigsParamsModel = {
    country,
    itemId,
    itemType,
  };

  const response = await getAxios(axiosInstance).get<CatalogConfigResponseModel>(URL, {
    params,
    withCredentials: false,
  });

  return selector(response.data);
};

const selector = (data: CatalogConfigResponseModel): CatalogConfigsData => {
  return data;
};

const { useData, prefetch, query } = createQuery<CatalogConfigsData, CatalogConfigsOptions>(
  'catalogConfigs',
  async (options, axiosInstance) => getCatalogConfigs({ ...options }, axiosInstance)
);

type CatalogConfigsHookOptions<SelectData> = Omit<CatalogConfigsOptions, 'country'> & {
  enabled?: boolean;
  select?: (data: CatalogConfigsData) => SelectData;
};

export const useCatalogConfigs = <SelectData = CatalogConfigsData>(
  options: CatalogConfigsHookOptions<SelectData>
) => {
  const { itemId, itemType, enabled = true, select } = options;

  const locale = useLocale();
  const country = getCountryFromLocale(locale);

  const catalogConfigAPIEnabled = useFeatureFlag('CATALOG_CONFIG_API_ENABLED') === 'true';

  return useData({
    enabled: enabled && catalogConfigAPIEnabled && Boolean(itemId) && Boolean(itemType),
    country,
    itemId,
    itemType,
    select,
  });
};

export const prefetchCatalogConfigs = (
  options: CatalogConfigsOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetch(queryClient, options, axiosInstance);
};

export const getCatalogConfigsFromCache = (
  options: CatalogConfigsOptions,
  queryClient: QueryClient
) => {
  return queryClient.getQueryData<CatalogConfigsData>(query.getFullKey(options));
};
